import { getMessaging, getToken } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';

let messaging = null;
try {
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    messaging = getMessaging();
    
    // Securely pass Firebase config to service worker
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.active) {
        // Only pass necessary config, not sensitive keys
        const publicConfig = {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
          appId: import.meta.env.VITE_FIREBASE_APP_ID
        };
        
        registration.active.postMessage({
          type: 'FIREBASE_CONFIG',
          config: publicConfig
        });
      }
    });
  }
} catch (error) {
  console.warn('Firebase messaging not available:', error);
}

const VAPID_KEY = 'BMqG5P1eUwtEaep2fbTkcNYK524VxdXe1wfNWXDKBo5kcEN1FRGuvr-VTCvShWSdO67o7RYcKbjA6lG-0D2gINc';

// Funzione per ottenere IP pubblico hashato
export async function getHashedIP() {
  try {
    // Prova prima con ipify
    let ip;
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      ip = data.ip;
    } catch (e) {
      // Fallback con ipapi
      try {
        const res2 = await fetch("https://ipapi.co/ip/");
        ip = await res2.text();
      } catch (e2) {
        // Fallback con httpbin
        const res3 = await fetch("https://httpbin.org/ip");
        const data3 = await res3.json();
        ip = data3.origin;
      }
    }

    if (!ip) throw new Error("No IP found");

    const encoder = new TextEncoder();
    const encoded = encoder.encode(ip.trim());
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    console.error("IP fetch failed", e);
    // Genera un hash basato su fingerprinting del browser come fallback
    const fingerprint = navigator.userAgent + navigator.language + screen.width + screen.height;
    const encoder = new TextEncoder();
    const encoded = encoder.encode(fingerprint);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return "fallback_" + hashArray.map(b => b.toString(16).padStart(2, "0")).join("").substring(0, 32);
  }
}

// Funzione per tracciare attività sospette
export async function trackActivity(activityType, metadata = {}) {
  try {
    const ipHash = await getHashedIP();
    if (!ipHash) return;

    const activityData = {
      ipHash,
      activityType,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      metadata,
      sessionId: sessionStorage.getItem('sessionId') || 'unknown'
    };

    // Salva in localStorage per analisi locale
    const activities = JSON.parse(localStorage.getItem('activityLog') || '[]');
    activities.push(activityData);
    // Mantieni solo gli ultimi 100 record
    if (activities.length > 100) activities.splice(0, activities.length - 100);
    localStorage.setItem('activityLog', JSON.stringify(activities));

    // Invia a Firestore
    await addDoc(collection(db, 'security_logs'), activityData);

  } catch (error) {
    console.error('Error tracking activity:', error);
  }
}

// Funzione per rilevare attività sospette
export function detectSuspiciousActivity() {
  const activities = JSON.parse(localStorage.getItem('activityLog') || '[]');
  const lastHour = new Date(Date.now() - 60 * 60 * 1000);

  const recentActivities = activities.filter(a => new Date(a.timestamp) > lastHour);

  // Troppi tentativi di login
  const loginAttempts = recentActivities.filter(a => a.activityType === 'login_attempt');
  if (loginAttempts.length > 10) {
    return { suspicious: true, reason: 'too_many_login_attempts', count: loginAttempts.length };
  }

  // Troppi errori
  const errors = recentActivities.filter(a => a.activityType === 'error');
  if (errors.length > 20) {
    return { suspicious: true, reason: 'too_many_errors', count: errors.length };
  }

  // Attività troppo veloce
  if (recentActivities.length > 100) {
    return { suspicious: true, reason: 'too_much_activity', count: recentActivities.length };
  }

  return { suspicious: false };
}

export const sendNotificationPermission = async (userId) => {
  try {
    if (!('Notification' in window)) {
      console.log('Notifications not supported');
      return null;
    }

    if (!window.isSecureContext) {
      console.log('Notifications require secure context');
      return null;
    }

    if (!messaging) {
      console.log('Firebase messaging not initialized');
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY
      });

      if (token) {
        await updateDoc(doc(db, 'users', userId), {
          fcmTokens: arrayUnion(token)
        });
        return token;
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting notification permission:', error);
    return null;
  }
};

export const showNotification = (title, options) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, options);
  }
};

export const onMessageListener = () => {
  return new Promise((resolve) => {
    if (messaging) {
      messaging.onMessage((payload) => {
        resolve(payload);
      });
    } else {
      console.warn('Messaging not available for foreground messages');
    }
  });
};

import { doc as doc2, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db as db2 } from "./firebase";
import axios from 'axios';
import md5 from 'md5';

// Prende iniziali da un nome
export const getInitials = (name) => {
  if (!name) return "A";
  return name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
};

// Stile consistente per avatar con iniziali
export const getAvatarStyle = (size = 42) => ({
  width: `${size}px`,
  height: `${size}px`,
  borderRadius: "50%",
  backgroundColor: "#000", // SEMPRE nero
  color: "#fff", // SEMPRE bianco
  fontWeight: "bold",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontSize: `${size * 0.4}px`,
  flexShrink: 0,
});

// Moderazione immagine tramite sistema unificato
export async function moderateImage(imageFile) {
  console.log("🔍 moderateImage: Inizio moderazione per file:", imageFile?.name || 'File senza nome');

  try {
    // Import unified moderation system
    const { moderateContent } = await import('./utils/contentModeration.js');
    
    let blob;
    if (imageFile instanceof File || imageFile instanceof Blob) {
      blob = imageFile;
    } else if (typeof imageFile === 'string' && imageFile.startsWith('data:image/')) {
      // Convert base64 to blob
      const base64Data = imageFile.split(',')[1];
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      blob = new Blob([bytes], { type: 'image/jpeg' });
    } else {
      throw new Error('Invalid image format');
    }
    
    // Use unified moderation system - avatar context for profile uploads
    const result = await moderateContent(blob, 'avatar');
    
    console.log("🛡️ Unified moderation result:", result.allowed);
    return result.allowed;

  } catch (error) {
    console.error("❌ Errore moderazione immagine:", error);
    
    // FAIL SAFE: Block content if moderation fails
    console.error("❌ ERRORE CRITICO: Moderazione fallita, BLOCCO upload per sicurezza");
    return false;
  }
}

// ✅ Lista parole vietate
const bannedWords = [
  "alcohol", "beer", "vodka", "whiskey", "wine", "rum",
  "porn", "sex", "gambling", "casino", "escort", "adult",
  "nude", "cigarettes", "smoke", "marijuana", "drugs"
];

// ✅ Nuova funzione più precisa per controllare parole vietate nei link
export function containsBannedWords(url) {
  try {
    const parsedUrl = new URL(url.toLowerCase());
    const domain = parsedUrl.hostname;
    const path = parsedUrl.pathname;

    return bannedWords.some(word => {
      const regex = new RegExp(`\\b${word}\\b`, "i"); // solo parola intera
      return regex.test(domain) || regex.test(path);
    });
  } catch (e) {
    return false; // se url malformato, ignora
  }
}

// ✅ Conta quante wishlist attive ha un utente (non archiviate)
export async function getActiveWishlistCount(userId) {
  const q = query(
    collection(db2, "wishlists"),
    where("createdBy", "==", userId),
    where("isArchived", "==", false)
  );
  const snapshot = await getDocs(q);
  return snapshot.size;
}

export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate();
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "ora";
  if (minutes < 60) return `${minutes}m fa`;
  if (hours < 24) return `${hours}h fa`;
  return `${days}g fa`;
};

export const validateImageSize = (file, maxSizeKB = 900) => {
  const maxSizeBytes = maxSizeKB * 1024;
  return file.size <= maxSizeBytes;
};

export const compressImage = (file, maxWidth = 800, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(resolve, 'image/jpeg', quality);
    };

    img.src = URL.createObjectURL(file);
  });
};